import { useEffect, useRef, type ReactNode } from 'react'

import { IconButton } from './Button.js'
import styles from './Sheet.module.css'

interface SheetProps {
  readonly open: boolean
  readonly title: string
  readonly onClose: () => void
  readonly children: ReactNode
}

/**
 * A modal sheet, on the platform `<dialog>` element.
 *
 * `showModal()` gives focus trapping, Escape-to-close, inertness of the page
 * behind, and top-layer stacking that no `z-index` can lose. Re-implementing
 * those is a well-known way to ship a dialog a screen reader can wander out of.
 */
export function Sheet({ open, title, onClose, children }: SheetProps) {
  const ref = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const dialog = ref.current
    if (!dialog) {
      return
    }
    // Calling showModal() on an already-open dialog throws, and close() on a
    // closed one is a no-op that still fires nothing — so check both.
    if (open && !dialog.open) {
      dialog.showModal()
    } else if (!open && dialog.open) {
      dialog.close()
    }
  }, [open])

  return (
    <dialog
      ref={ref}
      className={styles['sheet']}
      // Escape and the backdrop both close via the platform's own `cancel`
      // event, so the parent's state cannot drift out of sync with what the
      // user sees on screen.
      onCancel={(event) => {
        event.preventDefault()
        onClose()
      }}
      onClose={onClose}
    >
      <div className={styles['header']}>
        <h2 className={styles['title']}>{title}</h2>
        <IconButton label="Close" onClick={onClose}>
          <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
            <path
              d="M5 5l10 10M15 5L5 15"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
            />
          </svg>
        </IconButton>
      </div>
      <div className={styles['body']}>{children}</div>
    </dialog>
  )
}
